import { defineStore } from 'pinia'
import { computed, ref, watch, type Ref } from 'vue'
import { useStorage } from '@vueuse/core'
import type { Project, ProjectAccess, CreateProjectResponse, ProjectDetailResponse } from '@/core/types/api'
import { projectsApi } from '@/modules/projects/api'
import { useEntityStore } from '@/core/composables/useEntityStore'
import { useStoreOperation } from '@/core/composables/useStoreOperation'
import { useThemeStore } from '@/core/stores/useThemeStore'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { isNotFoundError } from '@/core/types/errors'
import { logger } from '@/core/utils/logger'
import { createPermissionCache, hasPermissionInCache } from '@/core/permissions/checker'

/**
 * Merges config entity data into a flat Project object.
 * Adapts the API response (nested configs) to the flat shape
 * that useProjectForm expects.
 */
function mergeConfigsIntoProject(response: ProjectDetailResponse): Project {
  const { project, mailerConfig, llmConfig, paymentConfig, formConfig, invoicingConfig } = response

  return {
    ...project,
    // LLM Config
    chatGptTranslationEnabled: llmConfig.chatGptTranslationEnabled,
    llmApiKey: llmConfig.llmApiKey,
    llmProvider: llmConfig.llmProvider,
    llmModel: llmConfig.llmModel,
    llmMaxTokens: llmConfig.llmMaxTokens,
    llmTimeout: llmConfig.llmTimeout,
    // Payment Config
    comgateMerchantId: paymentConfig.comgateMerchantId,
    comgateSecret: paymentConfig.comgateSecret,
    comgateTestMode: paymentConfig.comgateTestMode,
    // Form Config
    recaptchaSiteKey: formConfig.recaptchaSiteKey,
    recaptchaSecretKey: formConfig.recaptchaSecretKey,
    recaptchaEnabled: formConfig.recaptchaEnabled,
    // Invoicing Config
    invoicingEnabled: invoicingConfig.enabled,
    invoicingSupplierName: invoicingConfig.supplierName,
    invoicingSupplierAddress: invoicingConfig.supplierAddress,
    invoicingSupplierCity: invoicingConfig.supplierCity,
    invoicingSupplierPostalCode: invoicingConfig.supplierPostalCode,
    invoicingSupplierCountry: invoicingConfig.supplierCountry,
    invoicingSupplierCin: invoicingConfig.supplierCin,
    invoicingSupplierTin: invoicingConfig.supplierTin,
    invoicingSupplierVatPayer: invoicingConfig.supplierVatPayer,
    invoicingSupplierRegistrationNumber: invoicingConfig.supplierRegistrationNumber,
    invoicingSupplierAdditionalInfo: invoicingConfig.supplierAdditionalInfo,
    invoicingSupplierEmail: invoicingConfig.supplierEmail,
    invoicingSupplierPhone: invoicingConfig.supplierPhone,
    invoicingSupplierBankName: invoicingConfig.supplierBankName,
    invoicingSupplierBankAccount: invoicingConfig.supplierBankAccount,
    invoicingSupplierBankCode: invoicingConfig.supplierBankCode,
    invoicingSupplierIban: invoicingConfig.supplierIban,
    invoicingSupplierSwift: invoicingConfig.supplierSwift,
    invoicingInvoicePrefix: invoicingConfig.invoicePrefix,
    invoicingInvoiceDueDays: invoicingConfig.invoiceDueDays,
    invoicingInvoiceNote: invoicingConfig.invoiceNote,
    invoicingStampEnabled: invoicingConfig.stampEnabled,
    invoicingStampAsset: invoicingConfig.stampAsset,
    vyfakturujApiUrl: invoicingConfig.vyfakturujApiUrl,
    vyfakturujEmail: invoicingConfig.vyfakturujEmail,
    vyfakturujApiKey: invoicingConfig.vyfakturujApiKey,
    vyfakturujWebhookSecret: invoicingConfig.vyfakturujWebhookSecret,
    defaultNumberSeriesByProductType: invoicingConfig.defaultNumberSeriesByProductType ?? undefined,
    // Mailer Config
    mailerDsn: mailerConfig?.dsn ?? null,
    mailerFromAddress: mailerConfig?.fromAddress ?? null,
    mailerFromName: mailerConfig?.fromName ?? null,
    mailerToRecipient: mailerConfig?.toRecipient ?? null,
    mailerBccAddresses: mailerConfig?.bccAddresses ?? null,
  }
}

export const useProjectsStore = defineStore('projects', () => {
  // Použití generické implementace pro CRUD operace
  const entityStore = useEntityStore<Project, Partial<Project>, Partial<Project>>({
    entityName: 'Projekt',
    fetchEntities: async () => {
      const projects = await projectsApi.getProjects()
      return projects.map(item => item.project) || []
    },
    createEntity: async (data) => {
      // Note: This now returns CreateProjectResponse, but we extract only project for backwards compatibility
      const response = await projectsApi.createProject(data)
      return response.project
    },
    updateEntity: async (id, data) => {
      return await projectsApi.updateProject(id, data)
    }
  })

  // Grouped state: Project users management
  const usersState = ref<{
    items: ProjectAccess[]
    loading: boolean
    error: string | undefined
  }>({
    items: [],
    loading: false,
    error: undefined
  })

  // Grouped state: Current project context
  const currentProjectState = ref<{
    project: Project | null
    permissions: string[]
  }>({
    project: null,
    permissions: []
  })

  // Persisted project ID (separate for localStorage sync)
  const currentProjectId = useStorage<string>('currentProjectId', '')

  // List of all projects with their permissions
  const projectsWithPermissions = ref<{project: Project, permissions: string[]}[]>([])

  // Computed getters for backward compatibility
  const projectUsers = computed(() => usersState.value.items)
  const loadingUsers = computed(() => usersState.value.loading)
  const currentProject = computed(() => currentProjectState.value.project)
  const currentProjectPermissions = computed(() => currentProjectState.value.permissions)

  // Setup store operation handlers
  const { executeSilent } = useStoreOperation({
    loading: entityStore.loading,
    error: entityStore.error as unknown as Ref<string | undefined>
  })

  // Refs for useStoreOperation (needs writable refs)
  const usersLoadingRef = computed({
    get: () => usersState.value.loading,
    set: (v) => { usersState.value.loading = v }
  })
  const usersErrorRef = computed({
    get: () => usersState.value.error,
    set: (v) => { usersState.value.error = v }
  })

  const { executeSilent: executeSilentUsers } = useStoreOperation({
    loading: usersLoadingRef as unknown as Ref<boolean>,
    error: usersErrorRef as unknown as Ref<string | undefined>
  })

  // Vlastní výpočty specifické pro projekty
  const activeProjects = computed(() => {
    return entityStore.items.value.filter((project) => project.isActive)
  })

  /**
   * Cache projektových oprávnění jako Set pro rychlé O(1) lookup
   * Místo array.includes() (O(n)) používáme Set.has() (O(1))
   */
  const currentProjectPermissionsCache = computed(() => {
    return createPermissionCache(currentProjectState.value.permissions)
  })

  /**
   * Rychlá kontrola projektového oprávnění s využitím cache
   * @param permission Požadované oprávnění
   * @returns Boolean indikující, zda aktuální projekt má dané oprávnění
   */
  const hasProjectPermission = (permission: string): boolean => {
    return hasPermissionInCache(currentProjectPermissionsCache.value, permission)
  }

  // Fetch all projects with permissions
  const fetchAll = async (filters?: Record<string, unknown>) => {
    // Wait for auth to be ready before making API calls (prevents 401 on page refresh)
    const authStore = useAuthStore()
    if (!authStore.isAuthReady) {
      logger.log('Waiting for auth to be ready before fetchAll...')
      await new Promise<void>((resolve) => {
        const unwatch = watch(
          () => authStore.isAuthReady,
          (ready) => {
            if (ready) {
              unwatch()
              resolve()
            }
          },
          { immediate: true }
        )
      })
    }

    const search = filters?.search as string | undefined

    const result = await executeSilent(async () => {
      const projects = await projectsApi.getProjects(search)

      // Uložit projekty s oprávněními
      projectsWithPermissions.value = projects || []

      // Uložit projekty do entity store
      entityStore.items.value = projects ? projects.map((item: { project: Project; permissions: string[] }) => item.project) : []
      entityStore.dataFetched.value = true

      return projects
    })

    // Handle 404 specifically - reset state when projects not found
    if (!result && entityStore.error.value) {
      // Check if it's a 404 error using type-safe error checking
      if (isNotFoundError(entityStore.error.value)) {
        entityStore.dataFetched.value = false
        projectsWithPermissions.value = []
        entityStore.items.value = []
      }
      // Re-throw to maintain original behavior
      throw entityStore.error.value
    }

    return result
  }

  // Nové metody pro správu oprávnění
  const fetchProject = async (id: string) => {
    const result = await executeSilent(async () => {
      const response = await projectsApi.getProject(id)

      // Kontrola, zda projekt existuje
      if (!response?.project) {
        throw new Error('Projekt nebyl nalezen nebo byl smazán')
      }

      // Merge config data into flat Project object for form compatibility
      const mergedProject = mergeConfigsIntoProject(response)

      // Aktualizovat projekt ve store pokud již existuje
      const index = entityStore.items.value.findIndex(p => p.id === id)
      if (index >= 0) {
        entityStore.items.value[index] = mergedProject
      } else {
        entityStore.items.value.push(mergedProject)
      }

      // Update current project if this is the active project
      if (currentProjectState.value.project?.id === id) {
        currentProjectState.value.project = mergedProject
      }

      // Uložit oprávnění aktuálního uživatele
      currentProjectState.value.permissions = response.permissions

      return { project: mergedProject, permissions: response.permissions }
    })

    // Handle 404 specifically - remove project from store if deleted
    if (!result && entityStore.error.value) {
      if (isNotFoundError(entityStore.error.value)) {
        // Odstranit projekt ze store, pokud existuje
        entityStore.items.value = entityStore.items.value.filter(p => p.id !== id)
        projectsWithPermissions.value = projectsWithPermissions.value.filter(p => p.project.id !== id)
      }
      // Re-throw to maintain original behavior
      throw entityStore.error.value
    }

    return result
  }

  const fetchProjectUsers = async (projectId: string) => {
    return await executeSilentUsers(async () => {
      const users = await projectsApi.getProjectUsers(projectId)
      usersState.value.items = users
      return users
    })
  }

  const grantProjectAccess = async (projectId: string, userId: string, permissionsOrAccessLevel: string[] | string) => {
    try {
      const projectAccess = await projectsApi.grantAccess(projectId, userId, permissionsOrAccessLevel)

      // Aktualizovat seznam uživatelů v store
      const index = usersState.value.items.findIndex(u => u.user === userId)
      if (index >= 0) {
        usersState.value.items[index] = projectAccess
      } else {
        usersState.value.items.push(projectAccess)
      }

      return projectAccess
    } catch (error: unknown) {
      logger.error('Error granting project access:', error)
      throw error
    }
  }

  const removeProjectAccess = async (projectId: string, userId: string) => {
    try {
      await projectsApi.removeAccess(projectId, userId)

      // Odebrat uživatele ze seznamu
      usersState.value.items = usersState.value.items.filter(u => u.user !== userId)
    } catch (error: unknown) {
      logger.error('Error removing project access:', error)
      throw error
    }
  }

  // Přidání metody pro mazání projektů
  const deleteProject = async (id: string) => {
    return await executeSilent(async () => {
      await projectsApi.deleteProject(id)

      // Po úspěšném smazání odebrat projekt ze store
      entityStore.items.value = entityStore.items.value.filter(p => p.id !== id)
      projectsWithPermissions.value = projectsWithPermissions.value.filter(p => p.project.id !== id)

      return true
    })
  }

  // Rozšíření resetFetchState pro naše specifické potřeby
  const resetAllState = () => {
    // Základní reset
    entityStore.resetFetchState()

    // Reset grouped states
    usersState.value = { items: [], loading: false, error: undefined }
    currentProjectState.value = { project: null, permissions: [] }
    projectsWithPermissions.value = []
    entityStore.dataFetched.value = false
  }

  // Nové metody pro správu currentProject
  const themeStore = useThemeStore()
  
  const setCurrentProject = (project: Project) => {
    // Najdeme oprávnění pro projekt
    const projectWithPermissions = projectsWithPermissions.value.find(p => p.project.id === project.id)

    // Update grouped state atomically
    currentProjectState.value = {
      project,
      permissions: projectWithPermissions?.permissions || []
    }

    // Update theme configuration based on the project
    // Include logoUrl so it's available on login page (from localStorage)
    const themeConfigWithLogo = {
      ...project.themeConfig,
      logoUrl: project.logoUrl || undefined,
    }
    themeStore.updateThemeConfig(themeConfigWithLogo)

    // Persist only the project ID in localStorage (not the whole object with apiKey)
    currentProjectId.value = project.id
  }

  const initDefaultProject = async () => {
    // Try to load from localStorage first
    const savedProjectId = currentProjectId.value

    let projectId: string | null = null

    if (savedProjectId) {
      // Verify that the project exists in our list
      const projectWithPerms = projectsWithPermissions.value.find(p => p.project.id === savedProjectId)

      if (projectWithPerms) {
        setCurrentProject(projectWithPerms.project)
        projectId = projectWithPerms.project.id
      } else {
        // Project no longer exists, remove from localStorage
        currentProjectId.value = ''
      }
    }

    // Fall back to the first project if nothing was loaded from localStorage
    if (!projectId && projectsWithPermissions.value.length > 0) {
      setCurrentProject(projectsWithPermissions.value[0].project)
      projectId = projectsWithPermissions.value[0].project.id
    } else if (!projectId) {
      currentProjectState.value = { project: null, permissions: [] }
    }

    // Fetch full project detail to get config data (chatGptTranslationEnabled, etc.)
    if (projectId) {
      await fetchProject(projectId)
    }
  }

  const getCurrentPermissions = (): string[] => {
    return currentProjectState.value.permissions
  }

  // Wrapper pro useFormView - vrací jen project bez permissions
  const fetchOne = async (id: string) => {
    const response = await fetchProject(id)
    return response?.project || null
  }

  // Custom create method that returns full response with API key
  const createProject = async (data: Partial<Project>): Promise<CreateProjectResponse> => {
    const response = await executeSilent(async () => {
      const result = await projectsApi.createProject(data)
      // Add project to store
      entityStore.items.value.push(result.project)
      return result
    })

    if (!response) {
      throw new Error('Failed to create project')
    }

    return response
  }

  return {
    // Standardní rozhraní (konzistentní s ostatními stores)
    items: entityStore.items,
    fetchAll,
    fetchOne,
    delete: deleteProject,
    create: entityStore.create, // Legacy - returns only Project
    createProject, // New - returns CreateProjectResponse with apiKey
    update: entityStore.update,

    // Store state
    selectedProject: entityStore.selectedEntity,
    loading: entityStore.loading,
    error: entityStore.error,
    dataFetched: entityStore.dataFetched,
    resetFetchState: resetAllState,

    // Project-specific computed
    activeProjects,
    projectsWithPermissions,

    // Funkce pro správu oprávnění
    currentProjectPermissions,
    currentProjectPermissionsCache,
    hasProjectPermission,
    projectUsers,
    loadingUsers,
    fetchProjectUsers,
    grantProjectAccess,
    removeProjectAccess,

    // Funkce pro currentProject
    currentProject,
    setCurrentProject,
    initDefaultProject,
    getCurrentPermissions
  }
})
